/* LAKSHMI KANTHAM — fitted viewer with direct open/close and mobile swipes.
 * Destination: assets/zoom-dialog.js, duplicate theme 157698916489.
 * Based on the working direct-open/close replacement.
 * Adds single-finger horizontal photo navigation below 750px only.
 * Keeps thumbnail navigation, desktop scrolling and high-resolution loading.
 * Keep the working fit-to-screen and white-background CSS unchanged.
 */
import { Component } from '@theme/component';
import { prefersReducedMotion, debounce, preloadImage } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} media
 * @property {HTMLElement} thumbnails
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #swipeController;
  #swipe = null;
  #ignoreClickUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#swipeController = new AbortController();
    const signal = this.#swipeController.signal;
    const dialog = this.refs.dialog;
    // Capture touch gestures before the original drag-zoom wrapper.
    dialog.addEventListener('touchstart', this.#touchStart, { capture: true, passive: true, signal });
    dialog.addEventListener('touchmove', this.#touchMove, { capture: true, passive: false, signal });
    dialog.addEventListener('touchend', this.#touchEnd, { capture: true, passive: false, signal });
    dialog.addEventListener('touchcancel', () => { this.#swipe = null; }, { capture: true, signal });
    // A swipe must not trigger the original image-click-to-close action.
    dialog.addEventListener('click', (event) => {
      if (Date.now() < this.#ignoreClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#swipeController?.abort();
    this.#swipe = null;
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    super.disconnectedCallback();
  }

  /* LK MOBILE SWIPE: restricted to the large photo, below 750px.
   * Thumbnail taps and desktop/tablet vertical scrolling remain unchanged.
   */
  #touchStart = (event) => {
    this.#swipe = null;
    if (!window.matchMedia('(max-width: 749px)').matches || !this.refs.dialog.open) return;
    if (event.touches.length !== 1 || !(event.target instanceof Element)) return;
    const item = event.target.closest('.dialog-zoomed-gallery > .product-media-container--image');
    if (!item) return;
    const index = this.refs.media.indexOf(item);
    if (index < 0) return;
    const touch = event.touches[0];
    this.#swipe = { id: touch.identifier, x: touch.clientX, y: touch.clientY, index, horizontal: false };
  };

  #touchMove = (event) => {
    const swipe = this.#swipe;
    if (!swipe) return;
    if (event.touches.length !== 1) { this.#swipe = null; return; }
    const touch = Array.from(event.touches).find((item) => item.identifier === swipe.id);
    if (!touch) return;
    const dx = Math.abs(touch.clientX - swipe.x);
    const dy = Math.abs(touch.clientY - swipe.y);
    if (!swipe.horizontal && dy > 12 && dy > dx) {
      this.#swipe = null;
      return;
    }
    if (dx > 12 && dx > dy * 1.3) swipe.horizontal = true;
    if (swipe.horizontal) {
      if (event.cancelable) event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #touchEnd = (event) => {
    const swipe = this.#swipe;
    this.#swipe = null;
    if (!swipe) return;
    const touch = Array.from(event.changedTouches).find((item) => item.identifier === swipe.id);
    if (!touch) return;
    const dx = touch.clientX - swipe.x;
    const dy = touch.clientY - swipe.y;
    if (!swipe.horizontal || Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy) * 1.3) return;
    if (event.cancelable) event.preventDefault();
    event.stopImmediatePropagation();
    this.#ignoreClickUntil = Date.now() + 700;
    const nextIndex = swipe.index + (dx < 0 ? 1 : -1);
    // Stop at the first/last photo rather than looping unexpectedly.
    if (nextIndex < 0 || nextIndex >= this.refs.media.length) return;
    this.selectThumbnail(nextIndex, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  };

  /* DIRECT OPEN: no view-transition snapshot or transparent intermediate state. */
  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  open(index, event) {
    event?.preventDefault();
    const value = Number(index);
    const { dialog, media } = this.refs;
    if (!Number.isInteger(value) || !media[value]) return;
    dialog.classList.remove('dialog--closed', 'dialog--preloading');
    if (!dialog.open) dialog.showModal();
    // Also handles a single photo, where there is no thumbnail button.
    this.#scrollToMedia(media[value], 'instant');
    this.loadHighResolutionImage(media[value]);
    this.selectThumbnail(value, { behavior: 'instant' });
  }

  /* Mobile: scroll only the horizontal photo strip, not its ancestors. */
  #scrollToMedia(target, behavior) {
    if (window.matchMedia('(max-width: 749px)').matches) {
      const strip = target.closest('.dialog-zoomed-gallery');
      if (strip) {
        const stripRect = strip.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const left = strip.scrollLeft + targetRect.left - stripRect.left - strip.clientLeft;
        strip.scrollTo({ left, behavior });
        return;
      }
    }
    target.scrollIntoView({ behavior });
  }

  /* Original high-resolution image loading. */
  /** @param {HTMLElement} mediaContainer */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;
    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;
    preloadImage(highResolutionUrl);
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
  }

  /* Original scroll-based thumbnail selection. */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!this.refs.dialog.open || !media.length) return;
    const mostVisibleElement = await getMostVisibleElement(media);
    if (!mostVisibleElement || !this.refs.dialog.open) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /* DIRECT CLOSE: no thumbnail animation wait or image snapshot. */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    dialog.close();
    dialog.classList.remove('dialog--closed', 'dialog--preloading');
    window.dispatchEvent(new DialogCloseEvent());
  }

  /** @param {KeyboardEvent} event */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /** @param {number} index */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /** @param {number} index */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;
    this.loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   * @param {{behavior: ScrollBehavior}} options
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;
    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });
    const targetImage = media[index];
    if (targetImage) {
      this.#scrollToMedia(targetImage, options.behavior);
      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement | null>}
 */
function getMostVisibleElement(elements) {
  if (!elements.length) return Promise.resolve(null);
  return new Promise((resolve) => {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.length) return;
      const mostVisible = entries.reduce((prev, current) =>
        current.intersectionRatio > prev.intersectionRatio ? current : prev
      );
      observer.disconnect();
      resolve(/** @type {HTMLElement} */ (mostVisible.target));
    }, {
      threshold: Array.from({ length: 100 }, (_, i) => i / 100),
    });
    for (const element of elements) observer.observe(element);
  });
}
